import React, { useState } from 'react';
import { PropTypes } from 'prop-types';
import moment from 'moment';
import { connect } from 'react-redux';

import ModalWrapper from 'components/Modals/ModalWrapper';
import { RenderDatePicker } from 'components/Common/Inputs';
import DateTimes from 'components/Common/DateTimes';

const PublishedVersionsUpdateModal = ({
  title,
  isShown,
  onConfirm,
  onCancel,
  error,
  versionWindows,
  applicablePeriods,
  publishedVersionIndex,
  versionNumber
}) => {
  const defaultEffectiveFrom = publishedVersionIndex >= 0 && versionWindows && versionWindows.length > 0 && versionWindows[publishedVersionIndex].effectiveFrom;
  const defaultEffectiveTo = publishedVersionIndex >= 0 && versionWindows && versionWindows.length > 0 && versionWindows[publishedVersionIndex].effectiveTo;

  const [newEffectiveFromDate, setNewEffectiveFromDate] = useState(moment(defaultEffectiveFrom, 'YYYY-MM-DD').isValid() ? moment(defaultEffectiveFrom).format('MM-DD-YYYY') : '');
  const [newEffectiveToDate, setNewEffectiveToDate] = useState(moment(defaultEffectiveTo, 'YYYY-MM-DD').isValid() ? moment(defaultEffectiveTo).format('MM-DD-YYYY') : '');
  const overlappingDateCheck = (newEffectiveFromDateMoment, newEffectiveToDateMoment, currentWindow) => (newEffectiveFromDateMoment <= moment(currentWindow.effectiveTo, 'YYYY-MM-DD') || currentWindow.effectiveTo === null)
    && (newEffectiveToDateMoment === null || newEffectiveToDateMoment >= moment(currentWindow.effectiveFrom, 'YYYY-MM-DD'));

  const fullOverlappingDateCheck = (newEffectiveFromDateMoment, newEffectiveToDateMoment, currentWindow) => (newEffectiveFromDateMoment <= moment(currentWindow.effectiveFrom, 'YYYY-MM-DD') || currentWindow.effectiveTo === null)
    && (newEffectiveToDateMoment === null || newEffectiveToDateMoment >= moment(currentWindow.effectiveTo, 'YYYY-MM-DD'));

  const causesWindowStartToBePushed = (newEffectiveToDateMoment, currentWindow) => newEffectiveToDateMoment !== null
    && moment(currentWindow.effectiveFrom, 'YYYY-MM-DD').isSameOrBefore(newEffectiveToDateMoment, 'day')
    && (currentWindow.effectiveTo === null || moment(currentWindow.effectiveTo, 'YYYY-MM-DD').isAfter(newEffectiveToDateMoment, 'day'));

  const newEffectiveFromDateMoment = moment(newEffectiveFromDate, 'MM-DD-YYYY');
  const newEffectiveToDateMoment = newEffectiveToDate ? moment(newEffectiveToDate, 'MM-DD-YYYY') : null;

  const hasFromValue = newEffectiveFromDate !== '';
  const hasWindows = versionWindows != null;
  const fromIsValid = newEffectiveFromDateMoment.isValid();

  let overlappingCount = 0;
  let filteredCount = 0;
  let causesFutureDatedWindow = false;
  if (hasFromValue && hasWindows) {
    const otherWindows = versionWindows.filter(version => version.version !== versionNumber);
    overlappingCount = otherWindows.filter(version => fullOverlappingDateCheck(newEffectiveFromDateMoment, newEffectiveToDateMoment, version)).length;
    filteredCount = otherWindows.filter(version => overlappingDateCheck(newEffectiveFromDateMoment, newEffectiveToDateMoment, version)).length;

    if (newEffectiveToDateMoment) {
      const pushedStartMoment = newEffectiveToDateMoment.clone().add(1, 'day');
      causesFutureDatedWindow = pushedStartMoment.isAfter(moment(DateTimes.today), 'day')
        && otherWindows.some(version => causesWindowStartToBePushed(newEffectiveToDateMoment, version));
    }
  }

  // The published statement can not run on past the end of the applicable period that covers its start date
  const publishedStatementId = publishedVersionIndex >= 0 && versionWindows ? versionWindows[publishedVersionIndex]?.statementId : null;
  const coveringPeriod = hasFromValue && fromIsValid
    ? (applicablePeriods || []).find(period => period.withholdingStatementId === publishedStatementId
      && (!period.applicableFrom || moment(period.applicableFrom).isSameOrBefore(newEffectiveFromDateMoment, 'day'))
      && (!period.applicableTo || moment(period.applicableTo).isSameOrAfter(newEffectiveFromDateMoment, 'day')))
    : null;
  const maxEffectiveToMoment = coveringPeriod && coveringPeriod.applicableTo ? moment(coveringPeriod.applicableTo) : null;
  const exceedsApplicablePeriod = maxEffectiveToMoment !== null
    && (newEffectiveToDateMoment === null || newEffectiveToDateMoment.isAfter(maxEffectiveToMoment, 'day'));

  const isDisabled = hasFromValue && hasWindows && (!fromIsValid || overlappingCount > 0 || causesFutureDatedWindow || exceedsApplicablePeriod);
  const showInfoMessage = hasFromValue && hasWindows && fromIsValid && (overlappingCount > 0 || filteredCount > 0 || causesFutureDatedWindow);

  const disableConfirm = () => isDisabled;

  const onConfirmEffectiveDate = () => {
    onConfirm({ effectiveFromDate: newEffectiveFromDate, effectiveToDate: newEffectiveToDate, index: publishedVersionIndex, version: versionNumber });
  };

  return (
    <ModalWrapper
      modalSize="confirm-effective-date-modal"
      headerClose={ false }
      isOpen={ isShown }
      title={ title }
      btnConfirmTitle="Update"
      btnConfirmClass="btn-danger"
      btnCancelTitle="Cancel"
      onConfirm={ onConfirmEffectiveDate }
      onCancel={ onCancel }
      disableConfirm={ disableConfirm }
    >
      {exceedsApplicablePeriod && <h2 className="toast toast-red text-center bold">Effective To date can not be after {maxEffectiveToMoment.format('MM-DD-YYYY')}, when the statement stops being applicable
        </h2>}
      {showInfoMessage && !isDisabled && <div className="col-12">
        <h2 className="toast toast-blue text-center bold">Previous Statements are effective on these dates. Publishing this Statement will overwrite pre-existing information on the specified days
        </h2>
        </div>}
      {showInfoMessage && isDisabled && !exceedsApplicablePeriod && causesFutureDatedWindow
        && <h2 className="toast toast-red text-center bold">This change would push the start date of a published statement date into the future. Edit statement dates before updating
        </h2>}
      {showInfoMessage && isDisabled && !exceedsApplicablePeriod && !causesFutureDatedWindow
        && <h2 className="toast toast-red text-center bold">Effective dates would replace an existing published statement. Edit statement dates before updating
        </h2>}
      <div className="columns">
        <div className="col-6 effective-date-datepicker">
          <p><b>Effective From:</b> (required)</p>
          <RenderDatePicker
            input={ { onChange: e => setNewEffectiveFromDate(e), value: newEffectiveFromDate } }
            placeholder="mm-dd-yyyy"
            dateFormat="MM-dd-yyyy"
            maxDate={ newEffectiveToDate && new Date(Date.parse(newEffectiveToDate)) < DateTimes.today ? new Date(Date.parse(newEffectiveToDate)) : DateTimes.today }
          />
        </div>
        <div className="col-6 effective-date-datepicker pl-2">
          <p><b>Effective To:</b> (optional)</p>
          <RenderDatePicker
            input={ { onChange: e => setNewEffectiveToDate(e), value: newEffectiveToDate && newEffectiveToDate.length > 0 ? newEffectiveToDate : '' } }
            placeholder="mm-dd-yyyy"
            dateFormat="MM-dd-yyyy"
            isClearable
            minDate={ newEffectiveFromDate ? new Date(Date.parse(newEffectiveFromDate)) : new Date() }
            maxDate={ maxEffectiveToMoment ? maxEffectiveToMoment.toDate() : undefined }
          />
          <p>Unless a date is set the statement will be published until a new statement provided</p>
        </div>
      </div>
      {
                error
                && <div className="toast toast-error">
                    {error}
                </div>
            }
    </ModalWrapper>
  );
};

PublishedVersionsUpdateModal.propTypes = {
  title: PropTypes.string,
  isShown: PropTypes.bool,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  error: PropTypes.string,
  versionWindows: PropTypes.arrayOf(PropTypes.shape()),
  applicablePeriods: PropTypes.arrayOf(PropTypes.shape()),
  publishedVersionIndex: PropTypes.number,
  versionNumber: PropTypes.number,
};

PublishedVersionsUpdateModal.defaultProps = {
  title: null,
  isShown: false,
  error: null,
  onConfirm: null,
  onCancel: null,
  versionWindows: null,
  applicablePeriods: null,
  publishedVersionIndex: -1,
  versionNumber: -1
};

function mapStateToProps(state) {
  const {
    forms: {
      publishedVersionIndex,
      version
    }
  } = state;
  return {
    publishedVersionIndex,
    versionNumber: version
  };
}
export default connect(
  mapStateToProps,
  null
)(PublishedVersionsUpdateModal);
